import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import Card from '@material-ui/core/Card'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { IconPermIdentity } from '@components/atoms/icons'
import PropTypes from 'prop-types'
import ButtonCta from '@components/atoms/button-cta'
import Image from '@components/atoms/image'
import { gtmEvent } from '@lib/gtm-utils'
import * as amplitude from '@amplitude/analytics-browser'
import { useRouter } from 'next/router'
import Button from '@material-ui/core/Button'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import GolfCourseOutlinedIcon from '@material-ui/icons/GolfCourseOutlined'
import Divider from '@material-ui/core/Divider'
import { IconEventUpcoming } from '@components/atoms/icons/icon-event-upcoming'
import NoSsr from '@material-ui/core/NoSsr'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import CoachLogo from '@components/coach-logo'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import OpenInNewOutlinedIcon from '@material-ui/icons/OpenInNewOutlined'
import isPgaLink from '@utils/isPgaLink'
import useFeatureFlags from '@lib/feature-flags/useFeatureFlags'
import { flags } from '@lib/feature-flags/constants'
import useStyles from './coach-card.styles'

const CoachCard = ({
  coachInfo, isStory = false, source,
}) => {
  const {
    profilePhoto,
    displayName,
    city,
    state,
    customSlug,
    firstName,
    lastName,
    facilities,
    bookable,
    bookingURL,
    hasIntroVideo,
    objectID,
    memberType,
    certificationLevel,
    freeTierLimitsReached,
    hasPlusSubscription,
    randomInt,
    _rankingInfo,
    acceptsLeads,
  } = coachInfo
  const isLeadsLimitReached = freeTierLimitsReached?.includes('LEADS')
  const showContactForm = acceptsLeads && !isLeadsLimitReached
  const isExternalLink = !isPgaLink(bookingURL)
  const [isInfoVisualizationEnabled] = useFeatureFlags([flags.FLAG_COACH_SEARCH_INFO_VISUALIZATION])

  // Fields for Title will replace Facility Name
  // and a bio is added for the coach When the card is being used
  // in the story layout

  const isTablet = useMediaQuery(useTheme().breakpoints.between(600, 1200))
  const styles = useStyles({ isStory, isTablet, memberType })
  const router = useRouter()

  const [showBookNow, setShowBookNow] = useState(false)

  useEffect(() => {
    setShowBookNow(bookable && !isStory)
  }, [])

  const handleEventing = () => amplitude.track('click-book-now-button', {
    bookable,
    coach_profile_id: objectID,
    coach_name:
        firstName && lastName ? `${firstName} ${lastName}` : `${displayName}`,
    has_video: hasIntroVideo,
    source,
    page_path: router.asPath,
  })

  const LocationTag = () => (
    <Box display="flex" alignItems="center">
      <RoomOutlinedIcon className={styles.locationIcon} color="secondary" />
      <Typography className={styles.location} variant="body2" color="secondary" gutterBottom>
        {`${city}, ${state}`}
      </Typography>
    </Box>
  )

  const FacilityTag = () => {
    if (!facilities || facilities.length === 0) {
      return null
    }
    return (
      <Box display="flex" alignItems="center">
        <GolfCourseOutlinedIcon color="secondary" className={styles.locationIcon} />
        <Typography className={styles.location} variant="body2" color="secondary">
          {facilities[0].facilityName}
        </Typography>
      </Box>
    )
  }

  const ctaSelection = () => {
    if (isStory) {
      return (
        <ButtonCta
          variant="text"
          arrow
          className={styles.meetCoachButton}
          href={`/coach/${customSlug}`}
          dataAttrs={{
            'aria-label': `View profile for ${firstName} ${lastName}`,
          }}
        >
          Meet {firstName}
        </ButtonCta>
      )
    }
    const iconColor = memberType ? '#FAFAFA' : '#043362'

    return (
      <Box className={styles.ctaContainer}>
        <Button
          id="coach-card-view-profile-button"
          variant="text"
          size="small"
          color="primary"
          endIcon={<ChevronRightIcon fontSize="small" />}
          href={showContactForm ? `/coach/${customSlug}?openForm=true` : `/coach/${customSlug}`}
          dataAttrs={{
            'aria-label': showContactForm ? 'Send a Request' : 'View profile',
            'data-gtm-coach-search': true,
          }}
          style={{ color: '#043362' }}
        >
          {showContactForm ? 'Send a Request' : 'View Profile'}
        </Button>
        <Button
          id="coach-card-book-now-button"
          variant={memberType ? 'contained' : 'outlined'}
          startIcon={isExternalLink
            ? <OpenInNewOutlinedIcon color={iconColor} />
            : <IconEventUpcoming color={iconColor} />
          }
          className={styles.bookNowButton}
          size="small"
          href={bookingURL}
          target={isExternalLink ? '_blank' : ''}
          onClick={handleEventing}
          style={{ display: showBookNow ? 'inline-flex' : 'none' }}
          data-testid="book-now-button"
        >
          Book Now
        </Button>
      </Box>
    )
  }

  return (
    <NoSsr>
      <Card
        className={styles.root}
        onClick={() => gtmEvent({
          event: 'click-coach-card',
          attributes: {
            bookable,
            coach_name:
                firstName && lastName
                  ? `${firstName} ${lastName}`
                  : `${displayName}`,
            coach_profile_id: objectID,
            has_video: hasIntroVideo,
            source,
          },
        })
        }
      >
        <Link href={`/coach/${customSlug}`}>
          <a
            className={styles.contentContainer}
            data-gtm-coach-search
          >
            <Box display="flex" alignItems="center" mb={2}>
              {profilePhoto ? (
                <Image
                  data-testid="coachImage"
                  className={styles.coachImage}
                  src={profilePhoto}
                  alt={displayName}
                />
              ) : (
                <IconPermIdentity className={styles.coachImage} />
              )}
              <Box display="flex" flexDirection="column" alignItems="start">
                <Typography
                  className={styles.coachName}
                  variant="h6"
                  variantMapping={{ h6: 'h3' }}
                >
                  {firstName && lastName
                    ? `${firstName} ${lastName}`
                    : `${displayName}`}
                </Typography>
                <LocationTag />
                <FacilityTag />
                {isInfoVisualizationEnabled && (
                  <Box mt={1}>
                    {_rankingInfo?.matchedGeoLocation?.distance != null && (
                      <Typography variant="caption" display="block">
                        ~{Math.round(_rankingInfo.matchedGeoLocation.distance / 1609.34)} miles away
                      </Typography>
                    )}
                    {hasPlusSubscription && <Typography variant="caption" display="block">★ Plus Subscription</Typography>}
                    <Typography variant="caption" display="block">
                      Random Int: {randomInt}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box marginLeft="auto">
                <CoachLogo {...{ memberType, certificationLevel }} size="50px" />
              </Box>
            </Box>
            <Divider />
          </a>
        </Link>
        {ctaSelection()}
      </Card>
    </NoSsr>
  )
}

CoachCard.propTypes = {
  coachInfo: PropTypes.object,
  isStory: PropTypes.bool,
  hasIntroVideo: PropTypes.bool,
  source: PropTypes.string,
}

export default CoachCard
